import type { Perimeter } from '@atlassian/atl-context';

import {
	type CategorizedStorageControlsCache,
	CategoryStorageType,
	type ShouldEnforceKeyCallback,
} from '../../../types';
import type { CacheLoader } from '../load-storage-controls-data';

let analyticsEnabledGlobalState: boolean | undefined;
let productGlobalState: string | undefined;
let perimeterGlobalState: Perimeter | undefined;
let webStorageControlsEnabledGlobalState: boolean | undefined;
let indexedDbControlsEnabledGlobalState: boolean | undefined;
let disableWebStorageConsentChecksGlobalState: boolean | undefined;
let localConsentModeGlobalState: boolean | undefined;
let packageDebugModeEnabledGlobalState: boolean | undefined;
let allowUncategorizedCookiesInOverridesGlobalState: boolean | undefined;
let allowUncategorizedItemsInWebStorageOverridesGlobalState: boolean | undefined;
let disableIndexedDbConsentChecksGlobalState: boolean | undefined;
let __UNSAFE_shouldEnforceKeyForRolloutGlobalState: ShouldEnforceKeyCallback | undefined;
let userIdGlobalState: string | undefined;
let cloudIdGlobalState: string | undefined;
let hasInitialized: boolean = false;

let cookiesCacheLoader: CacheLoader | undefined;
let localStorageCacheLoader: CacheLoader | undefined;
let sessionStorageCacheLoader: CacheLoader | undefined;
let indexedDBCacheLoader: CacheLoader | undefined;

let loadCache: (
	storageType: CategoryStorageType,
) => Promise<CategorizedStorageControlsCache | undefined> = async (
	storageType: CategoryStorageType,
) => {
	switch (storageType) {
		case CategoryStorageType.Cookies:
			return await cookiesCacheLoader?.loadStorageControlsData();
		case CategoryStorageType.LocalStorage:
			return await localStorageCacheLoader?.loadStorageControlsData();
		case CategoryStorageType.SessionStorage:
			return await sessionStorageCacheLoader?.loadStorageControlsData();
		case CategoryStorageType.IndexedDB:
			return await indexedDBCacheLoader?.loadStorageControlsData();
		default:
			return undefined;
	}
};
let loadCacheMaybeSync: (
	storageType: CategoryStorageType,
) =>
	| CategorizedStorageControlsCache
	| undefined
	| Promise<CategorizedStorageControlsCache | undefined> = (storageType: CategoryStorageType) => {
	switch (storageType) {
		case CategoryStorageType.Cookies:
			return (
				cookiesCacheLoader?.getStorageControlsDataFromCache() ??
				cookiesCacheLoader?.loadStorageControlsData()
			);
		case CategoryStorageType.LocalStorage:
			return (
				localStorageCacheLoader?.getStorageControlsDataFromCache() ??
				localStorageCacheLoader?.loadStorageControlsData()
			);
		case CategoryStorageType.SessionStorage:
			return (
				sessionStorageCacheLoader?.getStorageControlsDataFromCache() ??
				sessionStorageCacheLoader?.loadStorageControlsData()
			);
		case CategoryStorageType.IndexedDB:
			return (
				indexedDBCacheLoader?.getStorageControlsDataFromCache() ??
				indexedDBCacheLoader?.loadStorageControlsData()
			);
		default:
			return undefined;
	}
};

export interface OverrideBscGlobalStateParams {
	loadCache?: typeof loadCache;
	loadCacheMaybeSync?: typeof loadCacheMaybeSync;
	product?: typeof productGlobalState;
}

export interface BscGlobalState {
	analyticsEnabled?: boolean;
	localConsentMode?: boolean;
	product?: string;
	perimeter?: Perimeter;

	webStorageEnabled?: boolean;
	indexedDbEnabled?: boolean;
	/**
	 * When `true` and `webStorageEnabled` is `true`, the web-storage passthrough installed for
	 * `localStorage` / `sessionStorage` runs in analytics-only mode: it still emits the usual
	 * analytics events for `setItem` calls, but it never blocks writes and the behaviour of
	 * `AtlBrowserStorage` is unchanged.
	 *
	 * Use this for telemetry-first rollouts where you want to measure usage of the controls
	 * pipeline without enforcing consent yet. This is the web-storage analogue of
	 * `disableIndexedDbConsentChecks`.
	 *
	 * Has no effect when `webStorageEnabled` is `false` (the passthrough is not installed).
	 *
	 * @default false
	 */
	disableWebStorageConsentChecks?: boolean;
	/**
	 * **Scope: cookies AND web storage** (despite the name).
	 *
	 * When `true`, both the `document.cookie` setter override AND the web-storage
	 * (`localStorage` / `sessionStorage`) `setItem` overrides will allow
	 * uncategorized items to pass through.
	 *
	 * If you only want to allow uncategorized writes for web storage without
	 * affecting cookies, use `allowUncategorizedItemsInWebStorageOverrides`
	 * instead.
	 *
	 * @default false
	 */
	allowUncategorizedCookiesInOverrides?: boolean;
	/**
	 * **Scope: web storage ONLY.**
	 *
	 * When `true` and `webStorageEnabled` is `true`, web-storage (`localStorage` /
	 * `sessionStorage`) `setItem` overrides will allow uncategorized items to pass
	 * through, the same way `allowUncategorizedCookiesInOverrides` does for web
	 * storage.
	 *
	 * Unlike `allowUncategorizedCookiesInOverrides` — which acts on **both**
	 * cookies and web storage — this flag has **no effect on cookies**. The
	 * `document.cookie` setter override never reads this value. Use this when you
	 * need to allow uncategorized writes for web storage without changing cookie
	 * behaviour.
	 *
	 * The two flags are independent and additive: web storage allows
	 * uncategorized writes when EITHER flag is `true`.
	 *
	 * Has no effect when `webStorageEnabled` is `false` (the passthrough is not
	 * installed).
	 *
	 * @default false
	 */
	allowUncategorizedItemsInWebStorageOverrides?: boolean;
	disableIndexedDbConsentChecks?: boolean;
	__UNSAFE_shouldEnforceKeyForRollout?: ShouldEnforceKeyCallback;

	packageDebugMode?: boolean;
	cookiesCacheLoader?: CacheLoader;
	localStorageCacheLoader?: CacheLoader;
	sessionStorageCacheLoader?: CacheLoader;
	indexedDBCacheLoader?: CacheLoader;
	hasInitialized?: boolean;
	userId?: string;
	cloudId?: string;

	loadCache: (
		storageType: CategoryStorageType,
	) => Promise<CategorizedStorageControlsCache | undefined>;
	loadCacheMaybeSync: (
		storageType: CategoryStorageType,
	) =>
		| CategorizedStorageControlsCache
		| undefined
		| Promise<CategorizedStorageControlsCache | undefined>;
}

export const getBscGlobalState: () => BscGlobalState = () => {
	return {
		analyticsEnabled: analyticsEnabledGlobalState ?? true,
		product: productGlobalState,
		perimeter: perimeterGlobalState,
		webStorageEnabled: webStorageControlsEnabledGlobalState ?? false,
		indexedDbEnabled: indexedDbControlsEnabledGlobalState ?? false,
		disableWebStorageConsentChecks: disableWebStorageConsentChecksGlobalState ?? false,
		localConsentMode: localConsentModeGlobalState ?? false,
		packageDebugMode: packageDebugModeEnabledGlobalState,
		allowUncategorizedCookiesInOverrides: allowUncategorizedCookiesInOverridesGlobalState ?? false,
		allowUncategorizedItemsInWebStorageOverrides:
			allowUncategorizedItemsInWebStorageOverridesGlobalState ?? false,
		disableIndexedDbConsentChecks: disableIndexedDbConsentChecksGlobalState ?? false,
		__UNSAFE_shouldEnforceKeyForRollout: __UNSAFE_shouldEnforceKeyForRolloutGlobalState,
		cookiesCacheLoader,
		localStorageCacheLoader,
		sessionStorageCacheLoader,
		indexedDBCacheLoader,
		loadCache,
		loadCacheMaybeSync,
		hasInitialized,
		userId: userIdGlobalState,
		cloudId: cloudIdGlobalState,
	};
};

export const testing_overrideGlobalState = ({
	loadCache: overrideLoadCache,
	loadCacheMaybeSync: overrideLoadCacheMaybeSync,
	product: overrideProduct,
}: OverrideBscGlobalStateParams): void => {
	if (overrideLoadCache) {
		loadCache = overrideLoadCache;
	}
	if (overrideLoadCacheMaybeSync) {
		loadCacheMaybeSync = overrideLoadCacheMaybeSync;
	}
	if (overrideProduct) {
		productGlobalState = overrideProduct;
	}
};

export const clearGlobalState = (): void => {
	analyticsEnabledGlobalState = undefined;
	productGlobalState = undefined;
	webStorageControlsEnabledGlobalState = undefined;
	indexedDbControlsEnabledGlobalState = undefined;
	disableWebStorageConsentChecksGlobalState = undefined;
	localConsentModeGlobalState = undefined;
	packageDebugModeEnabledGlobalState = undefined;
	allowUncategorizedCookiesInOverridesGlobalState = undefined;
	allowUncategorizedItemsInWebStorageOverridesGlobalState = undefined;
	disableIndexedDbConsentChecksGlobalState = undefined;
	__UNSAFE_shouldEnforceKeyForRolloutGlobalState = undefined;
	userIdGlobalState = undefined;
	cloudIdGlobalState = undefined;

	cookiesCacheLoader = undefined;
	localStorageCacheLoader = undefined;
	sessionStorageCacheLoader = undefined;
	indexedDBCacheLoader = undefined;
	hasInitialized = false;
};

/**
 * Internal: used only by initialize-global-state to mutate state.
 * Avoids circular dependency by keeping load-storage-controls-data and logger out of core.
 */
export interface InternalStateUpdates {
	productGlobalState?: string;
	webStorageControlsEnabledGlobalState?: boolean;
	indexedDbControlsEnabledGlobalState?: boolean;
	disableWebStorageConsentChecksGlobalState?: boolean;
	allowUncategorizedCookiesInOverridesGlobalState?: boolean;
	allowUncategorizedItemsInWebStorageOverridesGlobalState?: boolean;
	disableIndexedDbConsentChecksGlobalState?: boolean;
	__UNSAFE_shouldEnforceKeyForRolloutGlobalState?: ShouldEnforceKeyCallback;
	localConsentModeGlobalState?: boolean;
	analyticsEnabledGlobalState?: boolean;
	perimeterGlobalState?: Perimeter;
	cookiesCacheLoader?: CacheLoader;
	localStorageCacheLoader?: CacheLoader;
	sessionStorageCacheLoader?: CacheLoader;
	indexedDBCacheLoader?: CacheLoader;
	packageDebugModeEnabledGlobalState?: boolean;
	hasInitialized?: boolean;
	cloudIdGlobalState?: string;
	userIdGlobalState?: string;
}

export function __internalApplyInitialState(updates: InternalStateUpdates): void {
	if (updates.productGlobalState !== undefined) {
		productGlobalState = updates.productGlobalState;
	}
	if (updates.webStorageControlsEnabledGlobalState !== undefined) {
		webStorageControlsEnabledGlobalState = updates.webStorageControlsEnabledGlobalState;
	}
	if (updates.indexedDbControlsEnabledGlobalState !== undefined) {
		indexedDbControlsEnabledGlobalState = updates.indexedDbControlsEnabledGlobalState;
	}
	if (updates.disableWebStorageConsentChecksGlobalState !== undefined) {
		disableWebStorageConsentChecksGlobalState = updates.disableWebStorageConsentChecksGlobalState;
	}
	if (updates.allowUncategorizedCookiesInOverridesGlobalState !== undefined) {
		allowUncategorizedCookiesInOverridesGlobalState =
			updates.allowUncategorizedCookiesInOverridesGlobalState;
	}
	if (updates.allowUncategorizedItemsInWebStorageOverridesGlobalState !== undefined) {
		allowUncategorizedItemsInWebStorageOverridesGlobalState =
			updates.allowUncategorizedItemsInWebStorageOverridesGlobalState;
	}
	if (updates.disableIndexedDbConsentChecksGlobalState !== undefined) {
		disableIndexedDbConsentChecksGlobalState = updates.disableIndexedDbConsentChecksGlobalState;
	}
	if (updates.__UNSAFE_shouldEnforceKeyForRolloutGlobalState !== undefined) {
		__UNSAFE_shouldEnforceKeyForRolloutGlobalState =
			updates.__UNSAFE_shouldEnforceKeyForRolloutGlobalState;
	}
	if (updates.localConsentModeGlobalState !== undefined) {
		localConsentModeGlobalState = updates.localConsentModeGlobalState;
	}
	if (updates.analyticsEnabledGlobalState !== undefined) {
		analyticsEnabledGlobalState = updates.analyticsEnabledGlobalState;
	}
	if (updates.perimeterGlobalState !== undefined) {
		perimeterGlobalState = updates.perimeterGlobalState;
	}
	if (updates.cookiesCacheLoader !== undefined) {
		cookiesCacheLoader = updates.cookiesCacheLoader;
	}
	if (updates.localStorageCacheLoader !== undefined) {
		localStorageCacheLoader = updates.localStorageCacheLoader;
	}
	if (updates.sessionStorageCacheLoader !== undefined) {
		sessionStorageCacheLoader = updates.sessionStorageCacheLoader;
	}
	if (updates.indexedDBCacheLoader !== undefined) {
		indexedDBCacheLoader = updates.indexedDBCacheLoader;
	}
	if (updates.packageDebugModeEnabledGlobalState !== undefined) {
		packageDebugModeEnabledGlobalState = updates.packageDebugModeEnabledGlobalState;
	}
	if (updates.hasInitialized !== undefined) {
		hasInitialized = updates.hasInitialized;
	}
	if (updates.cloudIdGlobalState !== undefined) {
		cloudIdGlobalState = updates.cloudIdGlobalState;
	}
	if (updates.userIdGlobalState !== undefined) {
		userIdGlobalState = updates.userIdGlobalState;
	}
}

export function __internalGetHasInitialized(): boolean {
	return hasInitialized;
}

export function __internalGetProductGlobalState(): string | undefined {
	return productGlobalState;
}

export function __internalGetWebStorageControlsEnabledGlobalState(): boolean | undefined {
	return webStorageControlsEnabledGlobalState;
}

export function __internalGetIndexedDbControlsEnabledGlobalState(): boolean | undefined {
	return indexedDbControlsEnabledGlobalState;
}

export function __internalGetDisableWebStorageConsentChecksGlobalState(): boolean | undefined {
	return disableWebStorageConsentChecksGlobalState;
}

export function __internalGetAllowUncategorizedCookiesInOverridesGlobalState():
	| boolean
	| undefined {
	return allowUncategorizedCookiesInOverridesGlobalState;
}

export function __internalGetAllowUncategorizedItemsInWebStorageOverridesGlobalState():
	| boolean
	| undefined {
	return allowUncategorizedItemsInWebStorageOverridesGlobalState;
}

export function __internalGetShouldEnforceKeyGlobalState(): ShouldEnforceKeyCallback | undefined {
	return __UNSAFE_shouldEnforceKeyForRolloutGlobalState;
}

export function __internalGetDisableIndexedDbConsentChecksGlobalState(): boolean | undefined {
	return disableIndexedDbConsentChecksGlobalState;
}
